import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getHomeRouteByRole } from '../lib/rbac';
import { workspaceAdminAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const POLL_MS = 30000;

const mmss = (seconds) => {
  const s = Math.max(0, seconds);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * The standing reminder that the data on screen is not your own location's.
 *
 * Deliberately loud and un-dismissable either way: the risk of a borrowed
 * session is forgetting you are in one.
 *
 * Two kinds, and they must not read alike. An **operator** session is a platform
 * engineer inside a customer's workspace — hazard stripes, a countdown, "acting
 * as". A **location** session is a company's own admin viewing their own branch:
 * accurate, useful, and not an incident, so it gets a calm blue bar, no
 * countdown (there is no cap), and words that name the place. Telling an HR admin
 * they are "inside someone else's tenant" because they opened their Chandigarh
 * office is both false and alarming.
 *
 * For an operator session the countdown is the token's own remaining life fetched
 * from the server, not a client-side timer from when the dialog was submitted — a
 * page reload or a slow start must not buy extra minutes.
 */
const ProxySessionBar = () => {
  const { user, exitProxy } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [ending, setEnding] = useState(false);
  const expiredRef = useRef(false);

  const isProxy = Boolean(user?.isProxySession);

  const refresh = useCallback(async () => {
    if (!isProxy) return;
    try {
      const { data } = await workspaceAdminAPI.getProxySessionStatus();
      if (!data?.active) {
        setStatus(null);
        return;
      }
      setStatus(data);
      setSecondsLeft(data.expires_in_seconds);
    } catch {
      // A failed poll should not tear the banner down; the next tick retries.
    }
  }, [isProxy]);

  useEffect(() => {
    if (!isProxy) {
      setStatus(null);
      setSecondsLeft(null);
      expiredRef.current = false;
      return undefined;
    }
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [isProxy, refresh]);

  // Ticks locally between polls so the number moves; the server value is the
  // one that corrects it every 30s.
  useEffect(() => {
    if (secondsLeft === null) return undefined;
    const id = setInterval(() => setSecondsLeft((prev) => (prev === null ? null : Math.max(0, prev - 1))), 1000);
    return () => clearInterval(id);
  }, [secondsLeft === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const end = useCallback(async () => {
    setEnding(true);
    try {
      const response = await exitProxy();
      // Landing back on the signing-in account's own home, not the tenant route
      // we were impersonating on — that route belongs to a session that no
      // longer exists. `replace` so Back cannot walk into it either.
      navigate(getHomeRouteByRole(response?.role), { replace: true });
      toast.success(status?.kind === 'location' ? 'Back at your own location' : 'Proxy session ended');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not end the session'));
    } finally {
      setEnding(false);
    }
  }, [exitProxy, navigate, status?.kind]);

  const isLocation = status?.kind === 'location';

  // The token is dead at zero; leave rather than let someone sit on a screen
  // whose next request will 401. A location session has no cap, so there is no
  // zero to reach and nothing to kick them out of.
  useEffect(() => {
    if (isLocation || secondsLeft !== 0 || expiredRef.current || !isProxy) return;
    expiredRef.current = true;
    toast.info('The proxy session has expired');
    end();
  }, [secondsLeft, isProxy, isLocation, end]);

  if (!isProxy || !status) return null;

  if (isLocation) {
    return (
      <div
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 border-b border-blue-600/40 bg-blue-50 px-4 py-2.5 text-blue-950 dark:bg-blue-950/40 dark:text-blue-100"
        role="status"
        aria-live="polite"
      >
        <Building2 className="h-5 w-5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold">
            Viewing {status.tenant?.name} — another location of your organisation
          </p>
          <p className="truncate text-[11px] text-blue-900/80 dark:text-blue-200/80">
            You are signed in at {status.source_tenant_slug || 'your own location'}. Everything you
            do here is recorded against your own account.
          </p>
        </div>
        <span className="rounded-full bg-blue-950/10 px-3 py-1 font-mono text-[11px] whitespace-nowrap dark:bg-blue-100/10">
          {status.actions_recorded} action{status.actions_recorded === 1 ? '' : 's'} recorded
        </span>
        <button
          type="button"
          onClick={end}
          disabled={ending}
          className="shrink-0 rounded-md bg-blue-700 px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
        >
          {ending ? 'Returning…' : 'Back to my location'}
        </button>
      </div>
    );
  }

  const context = [
    status.tenant?.name,
    status.tenant?.vertical,
    status.region,
    status.reason_code ? `reason ${status.reason_code}` : null,
  ].filter(Boolean).join('  ·  ');

  const low = secondsLeft !== null && secondsLeft <= 120;

  return (
    <div
      className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 border-b border-amber-600/40 px-4 py-2.5 text-amber-950"
      style={{
        // Hazard stripes: this bar should never be mistaken for chrome.
        backgroundImage:
          'repeating-linear-gradient(135deg, rgb(251 191 36) 0 14px, rgb(245 178 11) 14px 28px)',
      }}
      role="status"
      aria-live="polite"
    >
      <Eye className="h-5 w-5 shrink-0" />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-bold">
          You are acting as a {String(status.acting_role || '').replace(/_/g, ' ')} in{' '}
          {status.tenant?.name}
        </p>
        <p className="truncate font-mono text-[11px] text-amber-900">{context}</p>
      </div>

      <span className="rounded-full bg-amber-950/10 px-3 py-1 font-mono text-[11px] whitespace-nowrap">
        {status.actions_recorded} action{status.actions_recorded === 1 ? '' : 's'} recorded
      </span>

      <span className="flex items-center gap-2 whitespace-nowrap font-mono text-[11px]">
        ends in
        <strong
          className={`rounded px-2 py-1 text-[15px] tabular-nums ${
            low ? 'bg-red-600 text-white' : 'bg-amber-950/10'
          }`}
        >
          {secondsLeft === null ? '--:--' : mmss(secondsLeft)}
        </strong>
      </span>

      <button
        type="button"
        onClick={end}
        disabled={ending}
        className="shrink-0 rounded-md bg-amber-950 px-3 py-1.5 text-[13px] font-semibold text-amber-50 transition-colors hover:bg-amber-900 disabled:opacity-60"
      >
        {ending ? 'Ending…' : 'End session'}
      </button>
    </div>
  );
};

export default ProxySessionBar;
